

import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { 
  Button, TextField, Typography, Box, Dialog, DialogActions, 
  DialogContent, DialogContentText, DialogTitle, CircularProgress, 
  IconButton, InputAdornment, Container, Paper, Stack, styled, 
  Stepper, Step, StepLabel, Fade
} from '@mui/material';
import { 
  Visibility, VisibilityOff, ArrowBackIosNew, 
  VpnKeyOutlined, MarkEmailReadOutlined, LockResetOutlined 
} from '@mui/icons-material';

/* ─── Design Tokens ─── */
const T = {
  amber:   '#C2823C',
  amberLight: '#E8A85A',
  cream:   '#FAF8F4',
  ivory:   '#FFFEFB',
  ink:     '#1a1510',
  inkLight:'#8a7055',
  border:  'rgba(194,130,60,0.18)',
  shadow:  '0 20px 60px rgba(100,70,30,0.12)',
};

/* ─── Styled Components ─── */
const PageWrapper = styled(Box)(() => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: T.cream,
  padding: '20px',
}));

const ResetCard = styled(Paper)(({ theme }) => ({
  background: T.ivory,
  border: `1px solid ${T.border}`,
  borderRadius: '32px',
  padding: theme.spacing(6, 4),
  width: '100%',
  maxWidth: '480px',
  boxShadow: T.shadow,
  textAlign: 'center',
}));

const PrimaryButton = styled(Button)(() => ({
  borderRadius: '50px',
  padding: '10px 24px',
  fontWeight: 600,
  textTransform: 'none',
  background: `linear-gradient(135deg, ${T.amber} 0%, ${T.amberLight} 100%)`,
  color: '#fff',
  '&:hover': {
    background: `linear-gradient(135deg, ${T.amberLight} 0%, ${T.amber} 100%)`,
  },
  '&:disabled': { background: '#ccc' }
}));

const steps = ['Verify Identity', 'Enter OTP', 'New Password'];

const PasswordReset = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // 0, 1, 2 for Stepper
  const [emailOrPhone, setEmailOrPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [message, setMessage] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // API URLs from process.env
  const requestResetApi = process.env.REACT_APP_REQUEST_RESET_PASSWORD_API;
  const verifyOtpApi = process.env.REACT_APP_VERIFY_OTP_API;
  const resetPasswordApi = process.env.REACT_APP_RESET_PASSWORD_API;

  const handleInputChange = (e) => {
    let value = e.target.value;
    if (!value.includes('@') && value.length === 10 && /^[0-9]+$/.test(value)) {
      value = `+91${value}`;
    }
    setEmailOrPhone(value);
  };

  const handleRequestOTP = async () => {
    setLoading(true);
    try {
      const res = await axios.post(requestResetApi, { email_or_phone: emailOrPhone });
      setMessage(res.data.message || "OTP Sent!");
      setStep(1);
    } catch (error) {
      setMessage(error.response?.data.error || 'Error requesting OTP');
      setIsDialogOpen(true);
    } finally { setLoading(false); }
  };

  const handleVerifyOTP = async () => {
    setLoading(true);
    try {
      const res = await axios.post(verifyOtpApi, { email_or_phone: emailOrPhone, otp });
      setStep(2);
    } catch (error) {
      setMessage(error.response?.data.error || 'Invalid OTP');
      setIsDialogOpen(true);
    } finally { setLoading(false); }
  };

  const handleResetPassword = async () => {
    if (newPassword !== confirmPassword) {
      setMessage("Passwords do not match");
      setIsDialogOpen(true);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(resetPasswordApi, { 
        email_or_phone: emailOrPhone, 
        new_password: newPassword,
        confirm_password: confirmPassword
      });
      setMessage("Password reset successful!");
      setIsDialogOpen(true);
      setTimeout(() => navigate('/login'), 2000);
    } catch (error) {
      setMessage(error.response?.data.error || 'Error resetting password');
      setIsDialogOpen(true);
    } finally { setLoading(false); }
  };

  return (
    <PageWrapper>
      <Fade in timeout={800}>
        <ResetCard elevation={0}>
          <Typography variant="h4" sx={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600, color: T.ink, mb: 1 }}>
            Security Reset
          </Typography>
          <Typography variant="body2" sx={{ color: T.inkLight, mb: 4 }}>
            Recover access to your VIMOIR account
          </Typography>

          <Stepper activeStep={step} alternativeLabel sx={{ mb: 5, '& .MuiStepIcon-root.Mui-active': { color: T.amber }, '& .MuiStepIcon-root.Mui-completed': { color: T.amber } }}>
            {steps.map((label) => (
              <Step key={label}><StepLabel>{label}</StepLabel></Step>
            ))}
          </Stepper>

          {/* Step 1: Request */}
          {step === 0 && (
            <Stack spacing={3}>
              <TextField
                fullWidth label="Email or Phone Number"
                value={emailOrPhone} onChange={handleInputChange}
                InputProps={{ startAdornment: <InputAdornment position="start"><MarkEmailReadOutlined sx={{color: T.amber}}/></InputAdornment> }}
              />
              <PrimaryButton onClick={handleRequestOTP} disabled={loading || !emailOrPhone}>
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Send Verification Code'}
              </PrimaryButton>
            </Stack>
          )}

          {/* Step 2: OTP */}
          {step === 1 && (
            <Stack spacing={3}>
              <TextField
                fullWidth label="6-Digit OTP"
                value={otp} onChange={(e) => setOtp(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><VpnKeyOutlined sx={{color: T.amber}}/></InputAdornment> }}
              />
              <Stack direction="row" spacing={2}>
                <Button fullWidth onClick={() => setStep(0)} startIcon={<ArrowBackIosNew />} sx={{ color: T.inkLight }}>Back</Button>
                <PrimaryButton fullWidth onClick={handleVerifyOTP} disabled={loading || !otp}>
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify Code'}
                </PrimaryButton>
              </Stack>
            </Stack>
          )}

          {/* Step 3: New Password */}
          {step === 2 && (
            <Stack spacing={2.5}>
              <TextField
                fullWidth label="New Password"
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><LockResetOutlined sx={{color: T.amber}}/></InputAdornment>,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowNewPassword(!showNewPassword)}>{showNewPassword ? <VisibilityOff /> : <Visibility />}</IconButton>
                    </InputAdornment>
                  )
                }}
              />
              <TextField
                fullWidth label="Confirm Password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
              />
              <Stack direction="row" spacing={2}>
                <Button fullWidth onClick={() => setStep(1)} sx={{ color: T.inkLight }}>Back</Button>
                <PrimaryButton fullWidth onClick={handleResetPassword} disabled={loading}>
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Update Password'}
                </PrimaryButton>
              </Stack>
            </Stack>
          )}

          <Button 
            onClick={() => navigate('/login')} 
            sx={{ mt: 4, textTransform: 'none', color: T.inkMid, fontSize: '0.85rem' }}
          >
            Cancel and Return to Login
          </Button>
        </ResetCard>
      </Fade>

      <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} PaperProps={{ sx: { borderRadius: '20px' } }}>
        <DialogTitle sx={{ color: T.amber }}>System Message</DialogTitle>
        <DialogContent><DialogContentText>{message}</DialogContentText></DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setIsDialogOpen(false)} variant="contained" sx={{ borderRadius: '50px', bgcolor: T.amber }}>Close</Button>
        </DialogActions>
      </Dialog>
    </PageWrapper>
  );
};

export default PasswordReset;